import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const Dashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/api/employees');
      setEmployees(data);
    } catch (err) {
      setError('Failed to load employee data. Please try again later.');
      console.error('Error fetching employees:', err);
    }
    setLoading(false);
  };

  const getStats = () => {
    const completed = employees.filter((e) => e.status === 'Completed').length;
    const pending = employees.filter((e) => e.status === 'Pending').length;
    const totalWithRating = employees.filter((e) => e.rating);
    const avgRating =
      totalWithRating.length > 0
        ? (
            totalWithRating.reduce((sum, e) => sum + e.rating, 0) /
            totalWithRating.length
          ).toFixed(2)
        : 0;

    return {
      total: employees.length,
      completed,
      pending,
      avgRating,
    };
  };

  const getCategoryCount = (category) => {
    return employees.filter((e) => e.category === category).length;
  };

  const stats = getStats();

  const styles = {
    container: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '24px',
      marginBottom: '32px',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease',
    },
    cardTitle: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#999',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '12px',
    },
    cardValue: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1a1a2e',
      lineHeight: '1.2',
    },
    cardSubtext: {
      fontSize: '12px',
      color: '#bbb',
      marginTop: '8px',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#1a1a2e',
      marginBottom: '20px',
      marginTop: '32px',
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '32px',
    },
    categoryCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      textAlign: 'center',
    },
    categoryLabel: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '8px',
    },
    categoryCount: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#e94560',
    },
    filterContainer: {
      display: 'flex',
      gap: '16px',
      marginBottom: '24px',
      flexWrap: 'wrap',
    },
    select: {
      padding: '10px 12px',
      borderRadius: '6px',
      border: '1px solid #e0e0e0',
      fontSize: '14px',
      backgroundColor: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    activityFeed: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    activityItem: {
      paddingBottom: '16px',
      marginBottom: '16px',
      borderBottom: '1px solid #f0f0f0',
    },
    activityItemLast: {
      borderBottom: 'none',
      marginBottom: 0,
    },
    activityTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1a1a2e',
      marginBottom: '4px',
    },
    activityTime: {
      fontSize: '12px',
      color: '#999',
    },
    statusBadge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      marginTop: '8px',
    },
  };

  const statusColors = {
    Completed: { bg: '#e8f5e9', color: '#2e7d32' },
    Pending: { bg: '#fff3e0', color: '#e65100' },
    Active: { bg: '#e3f2fd', color: '#1565c0' },
    Inactive: { bg: '#fce4ec', color: '#c62828' },
  };
  const getStatusColor = (status) => statusColors[status] || { bg: '#f5f5f5', color: '#666' };

  const getFilteredEmployees = () => {
    return employees.filter((emp) => {
      const categoryMatch =
        filters.category === 'all' || emp.category === filters.category;
      const statusMatch =
        filters.status === 'all' || emp.status === filters.status;
      return categoryMatch && statusMatch;
    });
  };

  if (error) {
    return (
      <div style={{ padding: '24px', color: '#d32f2f', backgroundColor: '#ffebee', borderRadius: '12px', marginBottom: '24px' }}>
        <strong>Error:</strong> {error}
      </div>
    );
  }

  return (
    <div>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Total Employees</div>
          <div style={styles.cardValue}>{stats.total}</div>
          <div style={styles.cardSubtext}>Across all departments</div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Pending Appraisals</div>
          <div style={styles.cardValue}>{stats.pending}</div>
          <div style={styles.cardSubtext}>
            {((stats.pending / stats.total) * 100).toFixed(0)}% of total
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Completed Appraisals</div>
          <div style={styles.cardValue}>{stats.completed}</div>
          <div style={styles.cardSubtext}>
            {((stats.completed / stats.total) * 100).toFixed(0)}% complete
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Average Rating</div>
          <div style={styles.cardValue}>{stats.avgRating}</div>
          <div style={styles.cardSubtext}>Organization-wide average</div>
        </div>
      </div>

      <div style={styles.sectionTitle}>Employee Distribution</div>
      <div style={styles.gridContainer}>
        <div style={styles.categoryCard}>
          <div style={styles.categoryLabel}>Production</div>
          <div style={styles.categoryCount}>{getCategoryCount('Production')}</div>
        </div>
        <div style={styles.categoryCard}>
          <div style={styles.categoryLabel}>Quality Controllers</div>
          <div style={styles.categoryCount}>{getCategoryCount('Quality Controllers')}</div>
        </div>
        <div style={styles.categoryCard}>
          <div style={styles.categoryLabel}>Project Managers</div>
          <div style={styles.categoryCount}>{getCategoryCount('Project Managers')}</div>
        </div>
      </div>

      <div style={styles.sectionTitle}>Filters</div>
      <div style={styles.filterContainer}>
        <select
          style={styles.select}
          value={filters.category}
          onChange={(e) =>
            setFilters({ ...filters, category: e.target.value })
          }
        >
          <option value="all">All Categories</option>
          <option value="Production">Production</option>
          <option value="Quality Controllers">Quality Controllers</option>
          <option value="Project Managers">Project Managers</option>
        </select>

        <select
          style={styles.select}
          value={filters.status}
          onChange={(e) =>
            setFilters({ ...filters, status: e.target.value })
          }
        >
          <option value="all">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Completed">Completed</option>
        </select>
      </div>

      <div style={styles.sectionTitle}>Recent Activity</div>
      <div style={styles.activityFeed}>
        {getFilteredEmployees().length > 0 ? (
          getFilteredEmployees().slice(0, 4).map((emp, idx) => {
            const filteredEmployees = getFilteredEmployees();
            return (
              <div
                key={emp.id}
                style={{
                  ...styles.activityItem,
                  ...(idx === filteredEmployees.slice(0, 4).length - 1 && styles.activityItemLast),
                }}
              >
                <div style={styles.activityTitle}>{emp.name}</div>
                <div style={styles.activityTime}>
                  {emp.designation} • {emp.category}
                </div>
                <div
                  style={{
                    ...styles.statusBadge,
                    backgroundColor: getStatusColor(emp.status).bg,
                    color: getStatusColor(emp.status).color,
                  }}
                >
                  {emp.status}
                </div>
                {emp.rating && (
                  <div style={{ ...styles.statusBadge, backgroundColor: '#e8eaf6', color: '#3f51b5', marginLeft: '8px' }}>
                    Rating: {emp.rating}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div style={{ color: '#999', textAlign: 'center', padding: '32px' }}>
            No employees match the selected filters.
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
